<template>
  <div class="hero-card">
    <div class="hero-avatar">
      <v-icon size="32" color="success">mdi-sync-circle</v-icon>
    </div>

    <p class="hero-title">{{ detail.package?.name || "Subscription" }}</p>
    <div class="hero-subtitle">
      <div class="hero-amount">
        {{ detail.plan?.amount != null ? formatCurrency(Number(detail.plan.amount)) : "-" }}
      </div>
      <div class="hero-interval text-capitalize">per {{ detail.recurring_subscription?.billing_interval || "-" }}</div>
    </div>

    <div v-if="detail.package?.highlight" class="hero-highlight">
      {{ detail.package.highlight }}
    </div>

    <div v-if="featureItems.length" class="feature-list">
      <p class="feature-title text-success">Features:</p>
      <div v-for="feature in featureItems" :key="feature" class="feature-item">
        <v-icon size="18" color="success" class="feature-icon">mdi-check-circle-outline</v-icon>
        <span class="feature-text">{{ feature }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { formatCurrency } from "@/shared/utils/utils";

type DetailStatus = "Active" | "Paused" | "Inactive" | string;

type SubscriptionDetailResponse = {
  recurring_subscription?: {
    id: string | number;
    status: DetailStatus;
    billing_interval: string | null;
    interval_count: number | null;
    start_date: string | null;
    end_date: string | null;
    next_billing_date: string | null;
    metadata: unknown;
  };
  package?: {
    id: string | number | null;
    name: string | null;
    package_type: string | null;
    highlight: string | null;
    features: string[] | null;
  };
  plan?: {
    id: string | number | null;
    amount: number | string | null;
    type_name: string | null;
    currency: string | null;
  };
  vendor?: {
    id: string | number | null;
    name: string | null;
    logo: string | null;
  };
  company?: {
    id: string | number | null;
    code: string | null;
    name: string | null;
    logo: string | null;
  };
};

const props = defineProps<{
  detail: SubscriptionDetailResponse;
}>();

const featureItems = computed(() =>
  Array.isArray(props.detail.package?.features)
    ? props.detail.package.features.filter(Boolean)
    : String((props.detail.package as { feature?: string | null } | undefined)?.feature ?? "")
        .split(/[,\n;]/)
        .map((item) => item.trim())
        .filter(Boolean),
);

</script>

<style scoped>
.hero-card {
  padding: 20px 16px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  background-color: #ffffff;
  border-radius: 18px;
  box-shadow: none;
}

.hero-card :deep(.v-card-text) {
  padding: 0;
}

.hero-avatar {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: rgba(46, 125, 50, 0.1);
  border: 1px solid rgba(46, 125, 50, 0.2);
}

.hero-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.hero-subtitle {
  margin: 0;
}

.hero-amount {
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
  color: #f97316;
}

.hero-interval {
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
}

.hero-highlight {
  width: 100%;
  margin-top: 2px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
  text-align: left;
}

.feature-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.feature-title {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  /* color: #475569; */
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  line-height: 1.35;
  color: #111827;
}

.feature-icon {
  flex: 0 0 auto;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  display: inline-block;
  background: #9ca3af;
}

.status-dot--active {
  background: #2e7d32;
}

.status-dot--paused {
  background: #f57c00;
}

.status-dot--inactive {
  background: #757575;
}

.status-text {
  font-size: 13px;
  font-weight: 700;
  color: #52606d;
  text-transform: capitalize;
}
</style>
